import { inject, Injectable } from '@angular/core';
import { TpaSearchFormated } from '../models/tpa-search.model';
import { HttpClient } from '@angular/common/http';
import {
  TpaSearchCoverageReq,
  TpaSearchCoverageRes,
  TpaSearchCoverageResExtended,
} from '../models/http/tpa-http.model';
import { environment } from '../../../../environments/environment';
import { InsuranceType, InsuranceTypeName, InsuredRole, InsuredRoleName } from '../../../model';
import { catchError, map, Observable, throwError } from 'rxjs';
import { SobResponse } from '../models/http/sob-http.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DateService } from '../../../shared/services/date.service';
import { StatusCalculationService } from '../../../shared/services/status-calculation.service';
import { TpaDetailSortService } from './tpa-detail-sort.service';

@Injectable({
  providedIn: 'root',
})
export class TpaHttpService {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private dateService = inject(DateService);
  private statusCalculationService = inject(StatusCalculationService);
  private tpaDetailSortService = inject(TpaDetailSortService);

  tpaSearchMember(search: TpaSearchFormated): Observable<TpaSearchCoverageResExtended[]> {
    const url = `${environment.API_URL}/CensusLines/SearchCoverage`;

    const params: TpaSearchCoverageReq = {
      firstName: search.firstName,
      lastName: search.lastName,
      memberId: search.memberId,
      policyNo: search.policyNumber,
      dateOfBirth: search.dateOfBirth || '',
      externalId: search.externalId || '',
    };

    const paramsWithValues = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value),
    ) as TpaSearchCoverageReq;

    return this.http.post<TpaSearchCoverageResExtended[]>(url, paramsWithValues).pipe(
      map((response: TpaSearchCoverageRes[]) => {
        const mappedResponse = response.map((item) => ({
          ...item,
          dateLeft: item.dateLeft ?? item.policyEndDate,
          insuredRoleName: InsuredRoleName[item.insuredRole as InsuredRole] || '',
          insuranceTypeName: InsuranceTypeName[item.insuranceType as InsuranceType] || '',
          cardConfig: {
            sections: [],
          },
          isActive: this.isActive(item.dateJoined, item.dateLeft),
          lineStatus: this.statusCalculationService.calculateLineStatus(
            item.dateJoined,
            item.dateLeft ?? item.policyEndDate,
          ),
          sobUploadedText: item.sobUploaded ? 'Uploaded ' : 'Not uploaded',
        }));

        return this.tpaDetailSortService.sortCensusLines(mappedResponse);
      }),
      catchError((error) => {
        this.errorMessage(error);
        return throwError(() => error);
      }),
    );
  }

  getScheduleOfBenefitsPlan(planId: string) {
    return this.http
      .get<SobResponse>(`${environment.API_URL}/Plans/ScheduleOfBenefits?planId=${planId}`)
      .pipe(
        catchError((error) => {
          this.errorMessage(error);
          return throwError(() => error);
        }),
      );
  }

  getScheduleOfBenefitsPlanDoc(planId: string) {
    return this.http
      .get(`${environment.API_URL}/Plans/sobdoc?planId=${planId}`, { responseType: 'blob' })
      .pipe(
        catchError((error) => {
          this.errorMessage(error);
          return throwError(() => error);
        }),
      );
  }

  getPolicyDoc(policyId: string) {
    return this.http
      .get(`${environment.API_URL}/Policies/document?policyId=${policyId}`, {
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          this.errorMessage(error);
          return throwError(() => error);
        }),
      );
  }

  private isActive(dateJoined: string, dateLeft: string): boolean {
    const today = this.dateService.formatToDateString(this.dateService.todayDate());
    const joinedDate = this.dateService.formatToDateString(dateJoined);
    const leftDate = dateLeft ? this.dateService.formatToDateString(dateLeft) : null;

    return joinedDate <= today && (!leftDate || leftDate > today);
  }

  private errorMessage(error: any) {
    const errorMessage = error?.error?.error ?? 'Something went wrong, please try later';
    this.snackBar.open(errorMessage, 'Close', {
      duration: 5000,
    });
  }
}
